import React, { useRef } from 'react';
import classnames from 'classnames';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Components
import WpPopover, { WpPopoverHeader, WpPopoverBody } from 'Wpds/components/popover-v1/WpPopover';
import SurfaceItemLogo from './SurfaceRowComponents/SurfaceItemLogo';
import SurfaceItemName from './SurfaceRowComponents/SurfaceItemName';
import SurfaceItemInsight from './SurfaceRowComponents/SurfaceItemInsight';
import SurfaceItemStatus from './SurfaceRowComponents/SurfaceItemStatus';
import SurfaceItemAvatars from './SurfaceRowComponents/SurfaceItemAvatars';
import SurfaceItemDetails from './SurfaceRowComponents/SurfaceItemDetails';
import AddSurfaceItemToWorkspaceModal from 'Shared/components/AddSurfaceItemToWorkspaceModal/AddSurfaceItemToWorkspaceModal';

// Types
import { Type } from 'Shared/types/Capture.types';
import { SurfaceAITableViewType } from './SurfaceAITable';

// Utils
import { getSurfaceTypeName } from './SurfaceAI.utils';

// Styles
import './SurfaceAITableItem.scss';

type Props = {
  surfaceItem;
  actions?: Function;
  viewType: SurfaceAITableViewType;
};

const SurfaceAITableItem = ({ surfaceItem, actions, viewType = SurfaceAITableViewType.TABLE }: Props) => {
  const [isAddToWorkspaceModalOpen, toggleIsAddToWorkspaceModalOpen] = useToggle(false);
  const hasActions = Boolean(actions);

  const baseClassName = 'SurfaceAITableItem';

  const TableView = () => {
    const itemRef = useRef(null);
    const getDetailViewTitle = () => {
      const { type, subtype } = surfaceItem;
      if (type === Type.SALESFORCE) return 'Opportunity';
      if (type === Type.GITHUB) return 'Pull Request';
      if (type === Type.JIRA) return surfaceItem.key;
      return getSurfaceTypeName({ type, subtype });
    };
    const detailViewTitle = getDetailViewTitle();

    const viewClass = `${baseClassName}__table-view`;
    const classes = classnames(baseClassName, viewClass);

    return (
      <>
        <div className={classes} ref={itemRef}>
          <div className={`${viewClass}__logo`}>
            <SurfaceItemLogo surfaceItem={surfaceItem} />
          </div>
          <div className={`${viewClass}__name`}>
            <SurfaceItemName surfaceItem={surfaceItem} />
          </div>
          <div className={`${viewClass}__insight`}>
            <SurfaceItemInsight surfaceItem={surfaceItem} />
          </div>
          <div className={`${viewClass}__status`}>
            <SurfaceItemStatus surfaceItem={surfaceItem} />
          </div>
          <div className={`${viewClass}__avatars`}>
            <SurfaceItemAvatars surfaceItem={surfaceItem} />
          </div>
          <div className={`${viewClass}__actions`}>{hasActions && <>{actions({ selectedItem: surfaceItem })}</>}</div>
        </div>
        <WpPopover target={itemRef} placement="bottom" size="lg" hideDelay={20}>
          <WpPopoverHeader centered>
            <div className={`${viewClass}__detail-logo`}>
              <SurfaceItemLogo surfaceItem={surfaceItem} />
            </div>
            {detailViewTitle}
          </WpPopoverHeader>
          <WpPopoverBody>
            <SurfaceItemDetails surfaceItem={surfaceItem} />
          </WpPopoverBody>
        </WpPopover>
      </>
    );
  };

  const ListView = () => {
    const viewClass = `${baseClassName}__list-view`;
    const classes = classnames(baseClassName, viewClass);
    return (
      <div className={classes} onClick={toggleIsAddToWorkspaceModalOpen}>
        {surfaceItem.suggestion?.new && <div className={`${viewClass}__new-indicator`} />}
        <div className={`${viewClass}__logo`}>
          <SurfaceItemLogo surfaceItem={surfaceItem} />
        </div>
        <div className={`${viewClass}__content`}>
          <div className={`${viewClass}__content__name`}>{surfaceItem.name}</div>
          <div className={`${viewClass}__content__status-avatars`}>
            <div className={`${viewClass}__content__status-avatars__status`}>
              <SurfaceItemStatus surfaceItem={surfaceItem} />
            </div>
            <div className={`${viewClass}__content__status-avatars__avatars`}>
              <SurfaceItemAvatars surfaceItem={surfaceItem} size="sm" limit={3} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {viewType === SurfaceAITableViewType.LIST ? <ListView /> : <TableView />}
      <AddSurfaceItemToWorkspaceModal
        isOpen={isAddToWorkspaceModalOpen}
        toggleModal={toggleIsAddToWorkspaceModalOpen}
        surfaceItem={surfaceItem}
      />
    </>
  );
};

export default SurfaceAITableItem;
